import { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

const n = (v) => {
  const f = parseFloat(v);
  return isNaN(f) ? 0 : f;
};
const orNull = (v) => (v === "" || v === null || v === undefined ? null : n(v));
const fmt = (v) => (v === null || v === undefined || v === "" ? "" : String(v));
const round = (v, d = 1) => (isFinite(v) ? Math.round(v * 10 ** d) / 10 ** d : 0);
const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const focusInput = (tid) => {
  const el = document.querySelector(`[data-testid="${tid}"]`);
  if (el) {
    el.focus();
    try { el.select(); } catch (_) {}
    return true;
  }
  return false;
};

const STAGES = [
  { key: "start", label: "Start", count: true, downtime: false },
  { key: "blow_mold", label: "Blow Mold", count: true, downtime: true },
  { key: "filler", label: "Filler", count: true, downtime: true },
  { key: "labeler", label: "Labeler", count: true, downtime: true },
  { key: "packer", label: "Packer", count: true, downtime: true },
  { key: "conveyor", label: "Conveyor", count: false, downtime: true },
  { key: "palletizer", label: "Palletizer", count: true, downtime: true },
];
const COUNT_STAGES = STAGES.filter((s) => s.count);
const DOWNTIME_STAGES = STAGES.filter((s) => s.downtime);
const DOWNTIME_KEYS = DOWNTIME_STAGES.map((s) => s.key);
const SCHED_KEYS = [
  ["change_over", "Change Over"],
  ["cip", "CIP"],
  ["break_lunch_cleanup", "Break/Lunch/Clean Up"],
];
const FINAL_STAGE = "palletizer";

const LINE_FIELDS = [
  { key: "po_number", label: "MO#", type: "text", tid: "line-po" },
  { key: "bottle_size", label: "Bottle Size", type: "bottle", tid: "line-bottle", wide: true },
  { key: "pack_size", label: "Pack Size", type: "pack", tid: "line-pack" },
  { key: "cases_per_pallet", label: "Cases / Pallet", type: "num", tid: "line-cpp" },
  { key: "label_name", label: "Label Name", type: "label", tid: "line-label", wide: true },
  { key: "cap_color", label: "Cap Color", type: "text", tid: "line-cap" },
  { key: "po_qty", label: "MO Qty.", type: "poqty", tid: "line-poqty" },
];

const normalize = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const BOTTLE_SIZES = [
  { label: "12oz | 355mL", oz: "12", pack: "choice" },
  { label: "16.9oz | 500mL", oz: "16.9", pack: "choice" },
  { label: "23.7oz | 700mL", oz: "23.7", pack: "24" },
  { label: "33.8oz | 1 L", oz: "33.8", pack: "12" },
];
const BPM_BY_OZ = { "12": 350, "16.9": 300, "33.8": 250 };
const bpmForBottle = (bottleLabel) => {
  const bs = BOTTLE_SIZES.find((b) => b.label === bottleLabel);
  return bs ? BPM_BY_OZ[bs.oz] ?? 0 : 0;
};

const emptyCounts = () => Object.fromEntries(COUNT_STAGES.map((s) => [s.key, ""]));
const emptyDowntime = () => Object.fromEntries(DOWNTIME_KEYS.map((k) => [k, ""]));
const emptyLine = () => ({
  _id: uid(),
  label_name: "",
  bottle_size: "",
  pack_size: "",
  cases_per_pallet: "",
  cap_color: "",
  po_number: "",
  po_qty: "",
  po_qty_unit: "bottles",
  counts: emptyCounts(),
  downtime: emptyDowntime(),
});

const cell =
  "w-full min-w-0 border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 text-center focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]";
const headCell =
  "border border-slate-200 bg-slate-100 px-2 py-2 text-xs font-semibold tracking-wide text-slate-700 text-center";

const NumCell = ({ value, onChange, testid, ro }) =>
  ro ? (
    <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{fmt(value) || "\u00A0"}</div>
  ) : (
    <input type="text" inputMode="decimal" className={cell} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  );

const TxtCell = ({ value, onChange, testid, ro }) =>
  ro ? (
    <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{value || "\u00A0"}</div>
  ) : (
    <input type="text" className={cell} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  );

function LabelSelect({ value, bottleSize, labels, onSelect, onDelete, ro, tid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const place = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 256) });
  };
  useEffect(() => {
    if (!open) return;
    place();
    const onScroll = () => setOpen(false);
    const onDoc = (e) => {
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);
  if (ro) return <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{value || "\u00A0"}</div>;
  const opts = bottleSize ? labels.filter((l) => l.bottle_size === bottleSize) : [];
  const q = query.trim().toLowerCase();
  const filtered = q ? opts.filter((l) => l.name.toLowerCase().includes(q) || (l.cap_color || "").toLowerCase().includes(q)) : opts;
  const pick = (l) => { onSelect(l); setQuery(""); setOpen(false); };
  return (
    <>
      <input
        ref={inputRef}
        type="text"
        className={cell}
        value={open ? query : value || ""}
        placeholder={bottleSize ? "Type to search…" : "Pick bottle size first"}
        disabled={!bottleSize}
        onFocus={() => { if (bottleSize) { setQuery(""); setOpen(true); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && open && filtered.length) { e.preventDefault(); pick(filtered[0]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        data-testid={tid}
      />
      {open && bottleSize && coords &&
        createPortal(
          <div
            ref={listRef}
            style={{ position: "fixed", left: coords.left, top: coords.top, width: coords.width }}
            className="z-[60] max-h-60 overflow-auto border border-slate-200 bg-white shadow-lg"
            data-testid={`${tid}-list`}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No labels found.</div>
            ) : (
              filtered.map((l) => (
                <div
                  key={l.id}
                  className="flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
                  onMouseDown={(e) => { e.preventDefault(); pick(l); }}
                  data-testid={`label-option-${l.id}`}
                >
                  <span className="truncate text-left">
                    {l.name}
                    {l.cap_color ? <span className="ml-1 text-xs text-slate-400">· {l.cap_color}</span> : null}
                  </span>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(l); }}
                    className="shrink-0 text-slate-300 hover:text-red-600"
                    data-testid={`delete-label-${l.id}`}
                    title="Delete permanently"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </>
  );
}

function LineCounts({ index, line, onCount, onDowntime, sched, onSched, totalSched, productionTime, onProdTime, onOverrideProdTime, totals, notes, onNotes, signature, onSignature, showScheduled, lineCount, ro }) {
  const [tptFocused, setTptFocused] = useState(false);
  const [tptDraft, setTptDraft] = useState("");
  const title = line.po_number ? `MO# ${line.po_number}` : `Line ${index + 1}`;
  const sub = [line.bottle_size, line.label_name].filter(Boolean).join(" · ");
  const cHead = "border border-slate-200 bg-slate-100 px-2 py-2 text-xs font-semibold tracking-wide text-slate-700 text-center";
  const cLabel = "flex items-center whitespace-nowrap border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-700";
  const cVal = "flex items-center justify-center border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-600 text-center";
  const cMuted = "flex items-center justify-center border border-slate-200 bg-slate-100/60 px-2 py-1.5 text-sm text-slate-300";
  const cInput = "w-full min-w-0 border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 text-center focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]";
  const grid = "grid grid-cols-[96px_78px_56px_84px] items-stretch gap-px";
  const eLbl = "flex items-center whitespace-nowrap border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-700";
  const eLblB = "flex items-center whitespace-nowrap border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-800";
  const eValB = "flex items-center justify-center border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-900";
  const eGrid = "grid grid-cols-[214px_88px] items-stretch gap-px";
  const onCellKey = (e, col, stageKey) => {
    const stages = (col === "count" ? COUNT_STAGES : STAGES).map((s) => s.key);
    const pos = stages.indexOf(stageKey);
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (pos > 0) focusInput(`${col}-${stages[pos - 1]}-${index}`);
      else if (index > 0) focusInput(`${col}-${stages[stages.length - 1]}-${index - 1}`);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (pos < stages.length - 1) focusInput(`${col}-${stages[pos + 1]}-${index}`);
      else if (index < lineCount - 1) focusInput(`${col}-${stages[0]}-${index + 1}`);
    } else if (e.key === "ArrowLeft") {
      if (e.target.selectionStart === 0 && col === "downtime") {
        if (focusInput(`count-${stageKey}-${index}`)) e.preventDefault();
      }
    } else if (e.key === "ArrowRight") {
      if (e.target.selectionStart === e.target.value.length && col === "count") {
        if (focusInput(`downtime-${stageKey}-${index}`)) e.preventDefault();
      }
    }
  };
  return (
    <div className="w-fit border border-slate-200 p-3" data-testid={`line-counts-${index}`}>
      <div className="mb-2.5">
        <span className="text-sm font-semibold text-slate-800" data-testid={`line-counts-title-${index}`}>{title}</span>
        {sub ? <span className="ml-2 text-xs text-slate-400">{sub}</span> : null}
      </div>
      <div className="flex gap-3">
        {/* Counts + Downtime table */}
        <div className="space-y-px">
          <div className={grid + " items-center"}>
            <span className={cHead + " text-left"}>Stage</span>
            <span className={cHead}>Count</span>
            <span className={cHead}>Loss</span>
            <span className={cHead + " whitespace-nowrap !px-1"}>Downtime</span>
          </div>
          {STAGES.map((s) => (
            <div key={s.key} className={grid}>
              <span className={cLabel}>{s.label}</span>
              {s.count ? (
                ro ? (
                  <div className={cVal}>{fmt(line.counts[s.key]) || "\u00A0"}</div>
                ) : (
                  <input type="text" inputMode="decimal" className={cInput} value={line.counts[s.key]} onChange={(e) => onCount(index, s.key, e.target.value)} onKeyDown={(e) => onCellKey(e, "count", s.key)} data-testid={`count-${s.key}-${index}`} />
                )
              ) : (
                <div className={cMuted}>—</div>
              )}
              {s.count ? (
                <div className={cVal} data-testid={`loss-${s.key}-${index}`}>{line.losses[s.key] === "" ? "\u00A0" : line.losses[s.key]}</div>
              ) : (
                <div className={cMuted}>—</div>
              )}
              {!s.downtime ? (
                <div className={cMuted}>—</div>
              ) : ro ? (
                <div className={cVal}>{fmt(line.downtime[s.key]) || "\u00A0"}</div>
              ) : (
                <input type="text" inputMode="decimal" className={cInput} value={line.downtime[s.key]} onChange={(e) => onDowntime(index, s.key, e.target.value)} onKeyDown={(e) => onCellKey(e, "downtime", s.key)} data-testid={`downtime-${s.key}-${index}`} />
              )}
            </div>
          ))}
          <div className={grid}>
            <span className="col-span-2 border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-800">Totals</span>
            <div className="flex items-center justify-center border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-900" data-testid={`total-loss-${index}`}>{round(line.totalLoss, 2)}</div>
            <div className="flex items-center justify-center border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-900" data-testid={`total-downtime-line-${index}`}>{round(line.downtimeTotal, 0)}</div>
          </div>
        </div>

        {/* Stockable table (vertical) + Scheduled Downtime */}
        <div className="flex h-full flex-col">
          <div className="flex gap-3">
            <div className="flex flex-col">
              <div className="grid grid-cols-[68px_92px] gap-px">
                <span className={cHead + " whitespace-nowrap !px-1 text-left"}>Stockable</span>
                <span className={cHead}>Counts</span>
              </div>
              {[
                ["Bottles", line.stockable_bottles ? round(line.stockable_bottles, 0).toLocaleString() : "", `stockable-bottles-${index}`],
                ["Cases", line.stockable_cases || "", `stockable-cases-${index}`],
                ["Pallets", line.stockable_pallets || "", `stockable-pallets-${index}`],
              ].map(([label, val, tid]) => (
                <div key={label} className="mt-px grid grid-cols-[68px_92px] items-stretch gap-px">
                  <span className={cLabel}>{label}</span>
                  <div className="flex items-center justify-center border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-800" data-testid={tid}>{val || "\u00A0"}</div>
                </div>
              ))}
            </div>
            {showScheduled && (
              <div className="self-start border border-slate-200 p-2 text-xs text-slate-500">
                <p className="mb-1 font-semibold uppercase tracking-[0.12em] text-slate-500">Standards</p>
                <p className="text-slate-600">12oz (355mL)</p>
                <p className="mb-1">350 BPM</p>
                <p className="text-slate-600">16.9oz (500mL)</p>
                <p className="mb-1">300 BPM</p>
                <p className="text-slate-600">33.8oz (1 Liter)</p>
                <p>250 BPM</p>
              </div>
            )}
          </div>
          {showScheduled && (
            <div className="mt-3">
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Scheduled Downtime (min)</h4>
              <div className="space-y-px">
                {SCHED_KEYS.map(([k, label]) => (
                  <div key={k} className="grid grid-cols-[160px_115px] items-stretch gap-px">
                    <span className="flex items-center border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-700">{label}</span>
                    {ro ? (
                      <div className={cVal}>{fmt(sched[k]) || "\u00A0"}</div>
                    ) : (
                      <input type="text" inputMode="decimal" className={cInput} value={sched[k]} onChange={(e) => onSched(k, e.target.value)} data-testid={`sched-${k}`} />
                    )}
                  </div>
                ))}
                <div className="grid grid-cols-[160px_115px] items-stretch gap-px">
                  <span className="flex items-center border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-800">Total Scheduled</span>
                  <div className="flex items-center justify-center border border-slate-200 bg-slate-100 px-2 py-1.5 text-sm font-semibold text-slate-900" data-testid="total-sched">{round(totalSched, 0)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {showScheduled && (
          <div>
            <div className="space-y-px">
              <div className={eGrid}>
                <span className={eLbl}>Production Time (min)</span>
                {ro ? <div className={cVal}>{fmt(productionTime) || "\u00A0"}</div> : <input type="text" inputMode="decimal" className={cInput} value={productionTime} onChange={(e) => onProdTime(e.target.value)} data-testid="production-time" />}
              </div>
              <div className={eGrid}>
                <span className={eLbl}>− Mechanical Downtime (min)</span>
                <div className={cVal} data-testid="total-mech">{round(totals.totalMech, 0)}</div>
              </div>
              <div className={eGrid}>
                <span className={eLbl}>− Scheduled Downtime (min)</span>
                <div className={cVal} data-testid="total-downtime-sched">{round(totals.totalSched, 0)}</div>
              </div>
              <div className={eGrid}>
                <span className={eLblB}>= Total Production Time</span>
                {ro ? (
                  <div className={eValB} data-testid="total-prod-time">{round(totals.totalProdTime, 0)}</div>
                ) : (
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full min-w-0 border border-slate-200 bg-slate-100 px-2 py-1.5 text-center text-sm font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                    value={tptFocused ? tptDraft : round(totals.totalProdTime, 0)}
                    onFocus={() => { setTptDraft(String(round(totals.totalProdTime, 0))); setTptFocused(true); }}
                    onChange={(e) => setTptDraft(e.target.value)}
                    onBlur={() => { setTptFocused(false); onOverrideProdTime(tptDraft); }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    title="Override to auto-adjust Production Time (if higher) or Scheduled Downtime (if lower)"
                    data-testid="total-prod-time"
                  />
                )}
              </div>
              <div className={eGrid}>
                <span className={eLbl}>× BPM (standard rate)</span>
                <div className={cVal} data-testid="bpm">{totals.bpm ? totals.bpm : "\u00A0"}</div>
              </div>
              <div className={eGrid}>
                <span className={eLblB}>= Standard</span>
                <div className={eValB} data-testid="standard">{round(totals.standard, 0).toLocaleString()}</div>
              </div>
              <div className={eGrid}>
                <span className={eLbl}>Total Stockable Bottles</span>
                <div className={cVal} data-testid="total-bottles">{round(totals.totalBottles, 0).toLocaleString()}</div>
              </div>
              <div className={eGrid}>
                <span className="flex items-center border border-[#002FA7] bg-[#002FA7]/10 px-2 py-1.5 text-sm font-bold text-[#002FA7]">% Efficiency</span>
                <div className="flex items-center justify-center border border-[#002FA7] bg-[#002FA7]/10 px-2 py-1.5 text-sm font-bold text-[#002FA7]" data-testid="efficiency">{round(totals.efficiency, 1)}%</div>
              </div>
            </div>
          </div>
        )}

        {showScheduled && (
          <div className="flex w-56 flex-col self-stretch">
            {ro ? (
              <div className="mb-2 min-h-[80px] flex-1 whitespace-pre-wrap border border-slate-200 px-2 py-1.5 text-sm text-slate-700">{notes || "\u00A0"}</div>
            ) : (
              <textarea className="mb-2 w-full flex-1 resize-none border border-slate-200 px-2 py-1.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7]" placeholder="Notes" value={notes} onChange={(e) => onNotes(e.target.value)} data-testid="report-notes" />
            )}
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Manager Signature</p>
            {ro ? (
              <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800">{signature || "\u00A0"}</div>
            ) : (
              <input type="text" className="w-full border border-slate-200 px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]" value={signature} onChange={(e) => onSignature(e.target.value)} placeholder="Type full name to sign" data-testid="report-signature" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductionReportForm({ initial, readOnly = false, onSave, onCancel, saving, products = [], labels = [], onDeleteLabel, mwoId, mos = [], coPackers = [] }) {
  const effMwoId = mwoId ?? initial?.mwo_id ?? null;
  const linkedMo = mos.find((m) => m.id === effMwoId) || null;
  const isAlum = /alum/i.test(linkedMo?.product_name || "");
  const [coPackerId, setCoPackerId] = useState(initial?.co_packer_id || "");
  const [date, setDate] = useState(initial?.date || todayLocal());
  const [shift, setShift] = useState(initial?.shift || "");
  const [lines, setLines] = useState(
    initial?.line_items?.length
      ? initial.line_items.map((l) => ({
          _id: uid(),
          label_name: fmt(l.label_name),
          bottle_size: fmt(l.bottle_size),
          pack_size: fmt(l.pack_size),
          cases_per_pallet: fmt(l.cases_per_pallet),
          cap_color: fmt(l.cap_color),
          po_number: fmt(l.po_number),
          po_qty: fmt(l.po_qty),
          po_qty_unit: fmt(l.po_qty_unit) || "bottles",
          counts: Object.fromEntries(COUNT_STAGES.map((s) => [s.key, fmt(l.production_counts?.[s.key])])),
          downtime: Object.fromEntries(DOWNTIME_KEYS.map((k) => [k, fmt(l.mechanical_downtime?.[k])])),
        }))
      : [emptyLine()]
  );
  const [sched, setSched] = useState(() => {
    const m = {};
    SCHED_KEYS.forEach(([k]) => (m[k] = fmt(initial?.scheduled_downtime?.[k])));
    return m;
  });
  const [productionTime, setProductionTime] = useState(initial ? fmt(initial.production_time) : "480");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [signature, setSignature] = useState(initial?.manager_signature || "");

  const derivedLines = useMemo(
    () =>
      lines.map((l) => {
        const counts = l.counts || {};
        const bottles = n(counts[FINAL_STAGE]);
        const pack = n(l.pack_size);
        const whole = pack > 0 ? Math.floor(bottles / pack) : 0;
        const rem = pack > 0 ? bottles - whole * pack : 0;
        const stockable_cases =
          pack > 0 && bottles > 0 ? (rem === 0 ? String(whole) : `${whole} + ${round(rem, 2)}/${pack}`) : "";
        const cpp = n(l.cases_per_pallet);
        const wholePallets = cpp > 0 ? Math.floor(whole / cpp) : 0;
        const remCases = cpp > 0 ? whole - wholePallets * cpp : 0;
        const stockable_pallets =
          cpp > 0 && whole > 0 ? (remCases === 0 ? String(wholePallets) : `${wholePallets} + ${round(remCases, 2)}/${cpp}`) : "";
        const losses = {};
        COUNT_STAGES.forEach((s, i) => {
          if (i === 0) { losses[s.key] = ""; return; }
          const prev = counts[COUNT_STAGES[i - 1].key];
          const cur = counts[s.key];
          if (prev === "" || cur === "" || prev == null || cur == null) { losses[s.key] = ""; return; }
          losses[s.key] = String(round(n(prev) - n(cur), 2));
        });
        const totalLoss = COUNT_STAGES.reduce((s, st) => s + (losses[st.key] === "" ? 0 : n(losses[st.key])), 0);
        const dt = l.downtime || {};
        const downtimeTotal = DOWNTIME_KEYS.reduce((s, k) => s + n(dt[k]), 0);
        return { ...l, stockable_bottles: bottles, stockable_cases, stockable_pallets, losses, totalLoss, downtimeTotal };
      }),
    [lines]
  );

  const totals = useMemo(() => {
    const totalMech = derivedLines.reduce((s, l) => s + l.downtimeTotal, 0);
    const totalSched = SCHED_KEYS.reduce((s, [k]) => s + n(sched[k]), 0);
    const totalDowntime = totalMech + totalSched;
    const totalProdTime = n(productionTime) - totalDowntime;
    const bpm = bpmForBottle(derivedLines.find((l) => l.bottle_size)?.bottle_size);
    const standard = totalProdTime * bpm;
    const totalBottles = derivedLines.reduce((s, l) => s + l.stockable_bottles, 0);
    const efficiency = standard > 0 ? (totalBottles / standard) * 100 : 0;
    return { totalMech, totalSched, totalDowntime, totalProdTime, bpm, standard, totalBottles, efficiency };
  }, [sched, productionTime, derivedLines]);

  const updateLine = (i, field, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const updateCount = (i, stageKey, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, counts: { ...l.counts, [stageKey]: value } } : l)));
  const updateDowntime = (i, stageKey, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, downtime: { ...l.downtime, [stageKey]: value } } : l)));
  const applyProdTimeOverride = (val) => {
    if (val === "" || val == null) return;
    const delta = round(n(val) - totals.totalProdTime, 2);
    if (!delta) return;
    if (delta > 0) setProductionTime(String(round(n(productionTime) + delta, 2)));
    else setSched((p) => ({ ...p, change_over: String(round(n(p.change_over) + -delta, 2)) }));
  };
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = () => {
    if (isAlum && !coPackerId) {
      toast.error("ALUM production consumes from a co-packer — select the co-packer that ran this production");
      return;
    }
    const payload = {
      date,
      shift,
      mwo_id: initial?.mwo_id ?? null,
      co_packer_id: coPackerId,
      line_items: derivedLines.map((l) => ({
        label_name: l.label_name,
        bottle_size: l.bottle_size,
        pack_size: orNull(l.pack_size),
        cases_per_pallet: orNull(l.cases_per_pallet),
        cap_color: l.cap_color,
        po_number: l.po_number,
        po_qty: orNull(l.po_qty),
        po_qty_unit: l.po_qty_unit || "bottles",
        production_counts: Object.fromEntries(COUNT_STAGES.map((s) => [s.key, orNull(l.counts[s.key])])),
        production_losses: Object.fromEntries(COUNT_STAGES.map((s) => [s.key, l.losses[s.key] === "" ? null : parseFloat(l.losses[s.key])])),
        mechanical_downtime: Object.fromEntries(DOWNTIME_KEYS.map((k) => [k, orNull(l.downtime[k])])),
        stockable_cases: l.stockable_cases || null,
        stockable_pallets: l.stockable_pallets || null,
        stockable_bottles: l.stockable_bottles || null,
      })),
      scheduled_downtime: Object.fromEntries(SCHED_KEYS.map(([k]) => [k, orNull(sched[k])])),
      production_time: orNull(productionTime),
      bpm: totals.bpm || null,
      total_mechanical_downtime: totals.totalMech,
      total_scheduled_downtime: totals.totalSched,
      total_production_time: totals.totalProdTime,
      total_stockable_bottles: totals.totalBottles,
      standard: totals.standard,
      efficiency: round(totals.efficiency, 2),
      notes,
      manager_signature: signature,
    };
    onSave(payload);
  };

  const RO = readOnly;

  const findProductForBottle = (bottleLabel) => {
    const bs = BOTTLE_SIZES.find((b) => b.label === bottleLabel);
    if (!bs) return null;
    const ozKey = normalize(bs.oz) + "oz";
    return products.find((p) => normalize(p.name).includes(ozKey)) || null;
  };
  const casesPerPalletFor = (bottleLabel, packSize) => {
    const prod = findProductForBottle(bottleLabel);
    const pack = n(packSize);
    if (!prod || prod.bottles_per_pallet == null || !pack) return "";
    return String(round(prod.bottles_per_pallet / pack, 2));
  };
  const onBottleChange = (i, bottleLabel) => {
    const bs = BOTTLE_SIZES.find((b) => b.label === bottleLabel);
    setLines((prev) =>
      prev.map((l, idx) => {
        if (idx !== i) return l;
        let pack = l.pack_size;
        if (bs && bs.pack !== "choice") pack = bs.pack;
        else if (!["12", "24"].includes(String(pack))) pack = "";
        const cpp = casesPerPalletFor(bottleLabel, pack);
        return { ...l, bottle_size: bottleLabel, pack_size: pack, cases_per_pallet: cpp !== "" ? cpp : l.cases_per_pallet };
      })
    );
  };
  const onPackChange = (i, packVal) => {
    setLines((prev) =>
      prev.map((l, idx) => {
        if (idx !== i) return l;
        const cpp = casesPerPalletFor(l.bottle_size, packVal);
        return { ...l, pack_size: packVal, cases_per_pallet: cpp !== "" ? cpp : l.cases_per_pallet };
      })
    );
  };
  const bottleCell = (i, tid) =>
    RO ? (
      <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{lines[i].bottle_size || "\u00A0"}</div>
    ) : (
      <select className={cell} value={lines[i].bottle_size} onChange={(e) => onBottleChange(i, e.target.value)} data-testid={tid}>
        <option value="">—</option>
        {BOTTLE_SIZES.map((b) => (
          <option key={b.label} value={b.label}>{b.label}</option>
        ))}
      </select>
    );
  const packCell = (i, tid) => {
    const bs = BOTTLE_SIZES.find((b) => b.label === lines[i].bottle_size);
    if (RO) return <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{fmt(lines[i].pack_size) || "\u00A0"}</div>;
    if (!bs) return <div className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-400 text-center" data-testid={tid}>—</div>;
    if (bs.pack !== "choice") return <div className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm text-slate-600 text-center" data-testid={tid}>{bs.pack}</div>;
    return (
      <select className={cell} value={lines[i].pack_size} onChange={(e) => onPackChange(i, e.target.value)} data-testid={tid}>
        <option value="">—</option>
        <option value="12">12</option>
        <option value="24">24</option>
      </select>
    );
  };

  const onLabelChange = (i, label) => {
    setLines((prev) =>
      prev.map((l, idx) =>
        idx === i ? { ...l, label_name: label.name, cap_color: label.cap_color ? label.cap_color : l.cap_color } : l
      )
    );
  };
  const handleDeleteLabel = (l) => {
    if (window.confirm(`Delete label "${l.name}" permanently from the options?`)) onDeleteLabel?.(l.id);
  };
  const labelCell = (i, tid) => (
    <LabelSelect
      value={lines[i].label_name}
      bottleSize={lines[i].bottle_size}
      labels={labels}
      ro={RO}
      tid={tid}
      onSelect={(l) => onLabelChange(i, l)}
      onDelete={handleDeleteLabel}
    />
  );

  const lineBottlesPerUnit = (l, unit) => {
    const pack = n(l.pack_size);
    const cpp = n(l.cases_per_pallet);
    const prod = findProductForBottle(l.bottle_size);
    const ppt = prod ? n(prod.pallets_per_truckload) : 0;
    switch (unit) {
      case "cases": return pack;
      case "pallets": return pack * cpp;
      case "truckloads": return pack * cpp * ppt;
      default: return 1; // bottles
    }
  };
  const onPoQtyUnitChange = (i, newUnit) => {
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const oldF = lineBottlesPerUnit(l, l.po_qty_unit || "bottles");
      const newF = lineBottlesPerUnit(l, newUnit);
      let po_qty = l.po_qty;
      if (oldF > 0 && newF > 0 && n(l.po_qty) > 0) po_qty = String(round((n(l.po_qty) * oldF) / newF, 2));
      return { ...l, po_qty, po_qty_unit: newUnit };
    }));
  };
  const poQtyCell = (i, tid) => {
    const l = lines[i];
    const unit = l.po_qty_unit || "bottles";
    if (RO) {
      const lbl = { bottles: "Bottles", cases: "Cases", pallets: "Pallets", truckloads: "Truckloads" }[unit];
      return <div className="border border-slate-200 px-2 py-1.5 text-sm text-slate-800 text-center">{fmt(l.po_qty) ? `${fmt(l.po_qty)} ${lbl}` : "\u00A0"}</div>;
    }
    return (
      <div className="flex items-stretch">
        <input type="text" inputMode="decimal" className={cell + " min-w-0 flex-1 border-r-0"} value={l.po_qty} onChange={(e) => updateLine(i, "po_qty", e.target.value)} data-testid={tid} />
        <select className={cell + " !w-[104px] flex-none"} value={unit} onChange={(e) => onPoQtyUnitChange(i, e.target.value)} data-testid={`${tid}-unit`}>
          <option value="bottles">Bottles</option>
          <option value="cases">Cases</option>
          <option value="pallets">Pallets</option>
          <option value="truckloads">Truckloads</option>
        </select>
      </div>
    );
  };

  const lineCell = (i, f, ns = "") => {
    const tid = `${f.tid}${ns}-${i}`;
    if (f.type === "bottle") return bottleCell(i, tid);
    if (f.type === "pack") return packCell(i, tid);
    if (f.type === "label") return labelCell(i, tid);
    if (f.type === "poqty") return poQtyCell(i, tid);
    if (f.type === "num")
      return <NumCell value={lines[i][f.key]} onChange={(v) => updateLine(i, f.key, v)} testid={tid} ro={RO} />;
    return <TxtCell value={lines[i][f.key]} onChange={(v) => updateLine(i, f.key, v)} testid={tid} ro={RO} />;
  };

  return (
    <div className="space-y-8" data-testid="production-report-form">
      {/* Date / Shift */}
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${isAlum || initial?.co_packer_id ? "md:grid-cols-3 sm:max-w-3xl" : "sm:max-w-md"}`}>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Date</label>
          {RO ? (
            <div className={cell}>{date || "\u00A0"}</div>
          ) : (
            <input type="date" className={cell} value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date" />
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">Shift</label>
          {RO ? (
            <div className={cell}>{shift || "\u00A0"}</div>
          ) : (
            <select className={cell} value={shift} onChange={(e) => setShift(e.target.value)} data-testid="report-shift">
              <option value="">—</option>
              <option value="1st">1st</option>
              <option value="2nd">2nd</option>
              <option value="3rd">3rd</option>
            </select>
          )}
        </div>
        {(isAlum || initial?.co_packer_id) && (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">
              Co-Packer {isAlum && !RO && <span className="text-red-600">*</span>}
            </label>
            {RO ? (
              <div className={cell} data-testid="report-copacker-ro">{coPackers.find((c) => c.id === (initial?.co_packer_id || coPackerId))?.company_name || initial?.co_packer_id || "\u00A0"}</div>
            ) : (
              <>
                <select className={cell} value={coPackerId} onChange={(e) => setCoPackerId(e.target.value)} data-testid="report-copacker">
                  <option value="">— select co-packer —</option>
                  {coPackers.map((c) => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">ALUM runs consume components from this co-packer's balance only — never main inventory.</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Line items */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 md:hidden">Line Items</h3>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto border border-slate-200 md:block">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {LINE_FIELDS.map((f) => (
                  <th key={f.key} className={headCell + (f.wide ? " min-w-[140px]" : "") + (f.key === "po_qty" ? " min-w-[210px]" : "")}>{f.label}</th>
                ))}
                {!RO && lines.length > 1 && <th className={headCell + " w-10"} />}
              </tr>
            </thead>
            <tbody>
              {derivedLines.map((l, i) => (
                <tr key={l._id} data-testid={`line-row-${i}`}>
                  {LINE_FIELDS.map((f) => (
                    <td key={f.key} className="p-0">{lineCell(i, f)}</td>
                  ))}
                  {!RO && lines.length > 1 && (
                    <td className="p-0 text-center align-middle">
                      <button onClick={() => removeLine(i)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`remove-line-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-4 md:hidden">
          {derivedLines.map((l, i) => (
            <div key={l._id} className="border border-slate-200 p-3" data-testid={`line-card-${i}`}>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Line {i + 1}</span>
                {!RO && lines.length > 1 && (
                  <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600" data-testid={`remove-line-m-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {LINE_FIELDS.map((f) => (
                  <div key={f.key} className={f.wide ? "col-span-2" : ""}>
                    <label className="mb-1 block text-[11px] font-medium text-slate-500">{f.label}</label>
                    {lineCell(i, f, "-m")}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!RO && (
          <Button variant="outline" className="mt-3 w-full rounded-sm border-dashed sm:w-auto" onClick={addLine} data-testid="add-line-button">
            <Plus className="mr-1 h-4 w-4" /> Add line
          </Button>
        )}
      </div>

      {/* Per-line Production Counts */}
      <div>
        <div className="space-y-4">
          {derivedLines.map((l, i) => (
            <LineCounts
              key={l._id}
              index={i}
              line={l}
              onCount={updateCount}
              onDowntime={updateDowntime}
              sched={sched}
              onSched={(k, v) => setSched((p) => ({ ...p, [k]: v }))}
              totalSched={totals.totalSched}
              productionTime={productionTime}
              onProdTime={setProductionTime}
              onOverrideProdTime={applyProdTimeOverride}
              totals={totals}
              notes={notes}
              onNotes={setNotes}
              signature={signature}
              onSignature={setSignature}
              showScheduled={i === 0}
              lineCount={derivedLines.length}
              ro={RO}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t border-slate-200 pt-6">
        <Button variant="outline" className="rounded-sm" onClick={onCancel} data-testid="report-cancel">
          {RO ? "Back" : "Cancel"}
        </Button>
        {!RO && (
          <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={saving} onClick={handleSave} data-testid="report-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Report"}
          </Button>
        )}
      </div>
    </div>
  );
}

